import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { logout } from "@services/krent.service";
import { removeCookie } from "@utils/cookies";
import { useSessionStorage } from "../../hooks";
import Logo from "@shared/Logo";

type Props = {
  notificationsCount: number;
  title?: string;
  subtitle?: string;
  showMobileSidebar?: boolean;
  setShowMobileSidebar?: (value: boolean) => void;
};

export const DashboardHeader = ({
  notificationsCount,
  title,
  subtitle,
  showMobileSidebar,
  setShowMobileSidebar,
}: Props) => {
  const [user, setUser] = useSessionStorage("user", {});
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getInitials = (firstname?: string, lastname?: string) => {
    const firstInitial = firstname?.[0] || "";
    const lastInitial = lastname?.[0] || "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const hasPhoto = user?.photo && user?.photo !== "default.jpg";
  const userInitials = getInitials(user?.firstname, user?.lastname);

  const handleLogout = () => {
    let abortController = new AbortController();
    logout(abortController.signal)
      .then(() => {
        setUser({});
        window && window.sessionStorage.clear();
        localStorage.removeItem("dontShowConversationModal");
        removeCookie("1Q_SPA");
        navigate("/", { replace: true, state: { from: location } });
      })
      .catch((e: any) => {
        console.log(e);
      });
  };

  return (
    <div className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center gap-3">
        <div className="d-lg-none">
          <button
            className="navbar-toggler border-0 cursor-pointer"
            type="button"
            onClick={() =>
              setShowMobileSidebar && setShowMobileSidebar(!showMobileSidebar)
            }
          >
            <span className="fa fa-bars fs-2 text-black"></span>
          </button>
        </div>
        <div className="d-lg-none">
          <Link className="navbar-brand fw-bolder align-middle" to="/">
            <Logo two width={"100px"} />
          </Link>
        </div>
        <div className="d-none d-lg-block">
          {title && (
            <div>
              <h3 className="fw-medium mb-1">{title}</h3>
              {subtitle && <p className=" fs-14 mb-0">{subtitle}</p>}
            </div>
          )}
        </div>
      </div>
      <div className="d-flex align-items-center gap-4">
        <div style={{ zIndex: "99999 !important" }}>
          <Link
            to="/notifications"
            className="navbar-user-link position-relative"
          >
            <span className="icon">
              <i
                className="fa-regular fa-bell "
                style={{ fontSize: "1.8rem", position: "relative" }}
              ></i>
            </span>
            {notificationsCount > 0 && (
              <span
                className="badge bg-danger position-absolute  start-100 translate-middle rounded-full py-1"
                style={{
                  fontSize: "0.65rem",
                  marginLeft: "-6px",
                  zIndex: 1,
                  bottom: 4,
                }}
              >
                {notificationsCount}
              </span>
            )}
          </Link>
        </div>
        <div className="position-relative" ref={dropdownRef}>
          <div
            className="d-flex align-items-center gap-2 cursor-pointer"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
              style={{
                backgroundColor: "#47BBA933",
                width: "40px",
                height: "40px",
                fontSize: "16px",
              }}
            >
              {hasPhoto ? (
                <img
                  src={user?.photo}
                  alt="Avatar"
                  style={{ objectFit: "cover" }}
                  height={40}
                  width={40}
                  className="rounded-circle"
                />
              ) : (
                <span>{userInitials}</span>
              )}
            </div>
            <span
              className="fs-10 fw-semibold text-dark text-truncate d-inline-block"
              style={{ maxWidth: "70px" }}
              title={`${user?.firstname || ""} ${user?.lastname || ""}`.trim()}
            >
              {user?.firstname} {user?.lastname}
            </span>
          </div>
          {showDropdown && (
            <div
              className="position-absolute end-0 mt-2 bg-white shadow rounded border"
              style={{ zIndex: 1000, minWidth: "180px" }}
            >
              <div className="py-1">
                <button
                  className="dropdown-item fs-14 text-dark px-4 py-2 text-start w-100"
                  onClick={() => navigate("/profile")}
                >
                  Profile
                </button>
                <button
                  className="dropdown-item fs-14 text-dark px-4 py-2 text-start w-100"
                  onClick={() => navigate("/settings")}
                >
                  Settings
                </button>
                <hr className="my-1" />
                <button
                  className="dropdown-item fs-14 text-danger px-4 py-2 text-start w-100"
                  onClick={handleLogout}
                >
                  <i className="fa-solid fa-right-from-bracket me-2 text-danger"></i>
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
